import * as React from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {useDestinationDisplayName} from 'hooks/useDestinationDisplayName'
import {useSeoAPMetadata} from 'hooks/useSeoAPMetadata'
import {useSeoSRPMetadata} from 'hooks/useSeoSRPMetadata'
import {getAnchorHotelName, getSearchType} from 'modules/sapiSearch/selectors'
import {SearchType} from 'modules/sapiSearch/utils'
import {getHotelName} from 'modules/search/selectors'
import Head from 'next/head'
import {SeoHrefLangLinks} from 'seo/hreflang'
import {IndexMetadata} from 'utils/server/searchSeo/getSeoMetadata'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {getSubBrandKeyFromUrl} from '@vio/core/src/_web/brand/utils'

const titles = defineMessages({
  placePage: {
    id: 'title.placePage',
    defaultMessage: 'Hotels in {place}'
  },
  placePageWithHotelNameFilter: {
    id: 'title.hotelNameFilter',
    defaultMessage: '{hotelName} in {place}'
  }
})

const description = defineMessages({
  legacyMetaDescription: {
    id: 'meta.description',
    defaultMessage:
      'Book the right hotel at the best price. Find unbelievable deals on hotels, all in one place. Save your money & time with {brandName}.'
  },
  metaDescription: {
    id: 'meta.descriptionVio',
    defaultMessage:
      'Get a better deal with {brandName}. Save money and get access to millions of stays; from cheap hotels to luxury villas.'
  }
})

const InjectPageHeadTags = () => {
  const {formatMessage} = useIntl()
  const hotelName = useSelector(getHotelName)
  const highlightedHotelName = useSelector(getAnchorHotelName)
  const destination = useDestinationDisplayName()
  const {
    brand,
    brandPageTitleMessage,
    brandPageTitleTagLineMessage,
    brandName
  } = useBrand()
  const subBrand = getSubBrandKeyFromUrl(brand)
  const searchType = useSelector(getSearchType)
  const metaSeoSRP = useSeoSRPMetadata()
  const metaSeoAP = useSeoAPMetadata()

  const generateTitleForPlacePage = (): string | null | undefined => {
    if (!destination) {
      return null
    }

    if (hotelName) {
      return formatMessage(titles.placePageWithHotelNameFilter, {
        brandName: brand.name,
        hotelName,
        place: destination
      })
    }

    return formatMessage(titles.placePage, {
      brandName: brand.name,
      place: destination
    })
  }

  const getTitleForPageType = () => {
    switch (searchType) {
      case SearchType.Hotel: {
        return highlightedHotelName || null
      }

      case SearchType.Place: {
        return generateTitleForPlacePage()
      }

      default: {
        return null
      }
    }
  }

  const getTitle = () => {
    const genericTitle = formatMessage(brandPageTitleMessage)
    const titleTagline = formatMessage(brandPageTitleTagLineMessage)
    const pageTitleForPageType = getTitleForPageType()

    if (metaSeoAP) {
      return metaSeoAP.title
    }

    if (metaSeoSRP) {
      return metaSeoSRP.title
    }

    if (pageTitleForPageType) {
      return `${brand.name} | ${pageTitleForPageType} | ${titleTagline}`
    }

    return `${subBrand ? subBrand : brand.name} | ${genericTitle}`
  }

  const getDescription = () => {
    if (metaSeoAP) {
      return metaSeoAP.description
    }

    if (metaSeoSRP) {
      return metaSeoSRP.description
    }

    return brandName === 'Vio'
      ? formatMessage(description.metaDescription, {brandName})
      : formatMessage(description.legacyMetaDescription, {brandName})
  }

  const renderSeoMetaTags = (metadata: IndexMetadata & SeoHrefLangLinks) => {
    return (
      <>
        {metadata.robots && <meta name="robots" content={metadata.robots} />}
        {metadata.canonical && (
          <link rel="canonical" href={metadata.canonical} />
        )}
        {metadata.hreflangLinks?.map(({href, hreflang}) => (
          <link
            key={hreflang}
            rel="alternate"
            href={href}
            hrefLang={hreflang}
          />
        ))}
      </>
    )
  }

  // Next's <Head> does not dedupe meta tags by `property`, so the Open Graph
  // tags are rendered here once instead of in _app.tsx.
  const social = metaSeoAP
    ? {
        title: metaSeoAP.openGraph.title,
        description: metaSeoAP.description,
        url: metaSeoAP.canonical,
        image: metaSeoAP.openGraph.imageUrl
      }
    : {
        title: brand.name,
        description: brand.metaDescription,
        url: brand.siteAddresses.address,
        image: `/static/brands/${brand.code}.png`
      }

  return (
    <Head>
      <title>{getTitle()}</title>
      <meta name="description" content={getDescription()} />
      {metaSeoSRP && renderSeoMetaTags(metaSeoSRP)}
      {metaSeoAP && renderSeoMetaTags(metaSeoAP)}
      <meta property="og:title" content={social.title} />
      <meta property="og:description" content={social.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={social.url} />
      <meta property="og:image" content={social.image} />
      {metaSeoAP && (
        // Head tags may sit at most one fragment deep, so the two size tags are not grouped
        <>
          {metaSeoAP.openGraph.imageSize && (
            <meta
              property="og:image:width"
              content={String(metaSeoAP.openGraph.imageSize[0])}
            />
          )}
          {metaSeoAP.openGraph.imageSize && (
            <meta
              property="og:image:height"
              content={String(metaSeoAP.openGraph.imageSize[1])}
            />
          )}
          <meta property="og:site_name" content={brand.name} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={social.title} />
          <meta name="twitter:description" content={social.description} />
          <meta name="twitter:image" content={social.image} />
        </>
      )}
    </Head>
  )
}

export default InjectPageHeadTags
